
import React, { useState } from 'react';
import { View, NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import { facialService } from '../../../services/central-api/facial';
import DeleteModal from '../../../components/DeleteModal';
import CustomButton from '../../../components/CustomButton';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { FacialParamList } from '../../../typings/Params';
import useFilters from '../../../components/Filter/hooks/useFilters';
import Toast from 'react-native-toast-message';
import { Pagination } from '../../../components/Pagination';
import MyDataTable from '../../../components/MyDataTable';
import FilterInput from '../../../components/Filter/components/FilterInput';
import MarkerModal from './MarkerModal';
import Filter from './Filters';
import DropdownButton from '../../../components/DropdownButton';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';

interface TagsParams {
    navigation: Navigation;
}

export default function Markers({ navigation }: TagsParams) {

    const route = useRoute<RouteProp<FacialParamList, 'Markers'>>();
    const [filters, setFilters] = useFilters<FacialParamList['Markers']>({
        ...route.params,
        page: Math.max(+route.params.page - 1, 0),
        limit: Number(route.params.limit) || 25,
    });

    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [selectedTag, setSelectedTag] = useState<BaseFacialTag>();
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);

    const isFocused = useIsFocused();
    const { data, isLoading, refetch } = useQuery({
        queryKey: ['getFacialMarkers', filters],
        queryFn: () => facialService.getFacialMarkers(filters),
        enabled: isFocused,
    });

    async function deleteMarker(tag: BaseFacialTag) {
        try {
            await facialService.deleteFacialMarker(tag.id);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            setIsDeleteModalVisible(false);
            setSelectedTag(undefined);
            refetch();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function handleFormClose() {
        setSelectedTag(undefined);
        setModalVisible(false);
        refetch();
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    return <Container>
        <Header
            title={translate('tags')}
            actions={<CustomButton text={translate('register')} onPress={() => {
                setSelectedTag(undefined);
                setModalVisible(true);
            }} />}
        />
        <Card>
            <DataTableHeader
                title={translate('registeredTags')}
                zIndex={2}
                filterInput={<FilterInput
                    value={textFilter}
                    placeholder={translate('typeToSearch')}
                    onChange={text => {
                        setTextFilter(text);
                    }}
                    onKeyPress={handleKeyDown}
                />}
                filter={<Filter
                    filters={filters}
                    setFilters={setFilters}
                    textFilter={textFilter}
                    setTextFilter={setTextFilter}
                />}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[{
                        name: 'ID',
                        selector: row => row.id,
                        grow: 0
                    },
                    {
                        name: translate('color'),
                        grow: 0,
                        cell: row => <View style={{ backgroundColor: row.color, height: 20, width: 20, borderRadius: 4 }}></View>
                    },
                    {
                        name: translate('name'),
                        selector: row => row.name,
                        wrap: true,
                        grow: 4
                    },
                    {
                        cell: row => <DropdownButton
                            actions={[
                                {
                                    content: translate('editInformation'),
                                    onPress: () => {
                                        setSelectedTag(row);
                                        setModalVisible(true);
                                    }
                                },
                                {
                                    content: translate('delete'),
                                    variant: 'danger',
                                    onPress: () => {
                                        setSelectedTag(row);
                                        setIsDeleteModalVisible(true);
                                    },
                                }
                            ]}
                        />,
                        wrap: true,
                        button: true,
                    }]}
                    data={data?.rows ?? []}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    currentPage={filters.page}
                    totalItems={data?.count ?? 0}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                />}
            />
        </Card>

        <MarkerModal
            isModalVisible={isModalVisible}
            setModalVisible={setModalVisible}
            marker={selectedTag}
            onClose={handleFormClose}
        />
        <DeleteModal
            onSubmit={async () => {
                try {
                    if (selectedTag) {
                        await deleteMarker(selectedTag);
                    }
                } catch (err) {
                    console.error(err);
                }
            }}
            isModalVisible={isDeleteModalVisible}
            itemName={selectedTag?.name}
            setModalVisible={() => setIsDeleteModalVisible(false)}
        />
    </Container>;
}
